import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Entity } from "@/domain/document-processing/types/entity-types";
import { EntityTable } from "./entity-table";
import {
  ChevronDown,
  Download,
  FileText,
  Loader2,
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { Button } from "./button";
import { Badge } from "./badge";
import { EntityExportService } from '@/domain/shared/services/EntityExportService';
import { useToast } from '@/hooks/use-toast';
import { useDocumentStore } from '@/stores/useDocumentStore';
import { useProductOrderStore } from '@/domain/product-orders/stores/useProductOrderStore';
import { sourceReviewReason, type SourceEvidenceSelection } from './source-evidence';
import { latestHumanReviews, screeningLabel } from '@/types/identity-review-types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  unresolvedVisualIdentityLabel,
} from '@/types/document-types';

const SourceEvidenceDialog = React.lazy(() => import('./source-evidence-dialog').then(module => ({ default: module.SourceEvidenceDialog })));

interface InitialEntitiesProps {
  entities: Entity[];
  sectionTitle?: string;
  onDelete?: (entityId: string) => void;
  onEdit?: (entity: Entity) => void;
  defaultOpen?: boolean;
  exportTarget?: HTMLElement | null;
}

export function InitialEntities({ 
  entities, 
  sectionTitle = "Parties", 
  onDelete, 
  onEdit, 
  defaultOpen = true,
  exportTarget
}: InitialEntitiesProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null); // Ref for the table container
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const { toast } = useToast();
  const documentMetadata = useDocumentStore((s) => s.documentMetadata);
  const results = useDocumentStore(state => state.results);
  const consolidatedReviews = useDocumentStore(state => state.consolidationResult?.humanIdentityReviews);
  const humanHistory = React.useMemo(() => Array.from(new Map([
    ...(consolidatedReviews ?? []), ...results.flatMap(result => result.humanIdentityReviews ?? []),
  ].map(review => [review.id, review])).values()), [consolidatedReviews, results]);
  const humanReviews = React.useMemo(() => latestHumanReviews(humanHistory || []), [humanHistory]);
  const orderId = useProductOrderStore(state => state.currentOrder?.id);
  const [selectedSource, setSelectedSource] = useState<(SourceEvidenceSelection & { orderId: string }) | null>(null);
  React.useEffect(() => { setSelectedSource(null); }, [orderId, results]);
  // A name a reviewer has already confirmed moves to the confirmed list.
  const unresolvedSources = React.useMemo(() => {
    const confirmed = new Set(humanReviews.map(review => `${review.documentId}:${review.sourceIdentity.occurrenceId}`));
    return results.flatMap(result => (result.unresolvedVisualIdentities ?? [])
      .filter(identity => !confirmed.has(`${result.documentId}:${identity.occurrenceId}`))
      .map(identity => ({ identity, documentId: result.documentId, storageDocumentId: result.storageDocumentId, fileName: result.fileName })));
  }, [results, humanReviews]);
  const unresolvedVisualIdentities = React.useMemo(() => results.flatMap(result => result.unresolvedVisualIdentities ?? []), [results]);
  
  if (!entities.length && !unresolvedVisualIdentities.length && !humanReviews.length) return null;
  
  const currentDisplayEntities = entities;
  const currentSectionTitle = sectionTitle;

  const handleExport = async (format: 'json' | 'md' | 'pdf') => {
    if (
      entities.length === 0
      && unresolvedVisualIdentities.length === 0 && humanReviews.length === 0
    ) {
      toast({
        title: "No entities to export",
        description: "There are no entities available to export.",
        variant: "destructive"
      });
      return;
    }

    setIsExporting(format);
    
    try {
      let result;
      let exportDescription;
      
      if (format === 'json') {
        result = await EntityExportService.exportToJson(
          entities,
          unresolvedVisualIdentities,
          humanHistory || []
        );
        exportDescription = `${entities.length} entities exported to ${result.filename}`;
      } else if (format === 'md') {
        result = await EntityExportService.exportToMd(
          entities,
          unresolvedVisualIdentities,
          humanHistory || []
        );
        exportDescription = `${entities.length} entities exported to ${result.filename}`;
      } else if (format === 'pdf') {
        // Get the HTML content from the rendered table
        const report = tableContainerRef.current?.cloneNode(true) as HTMLElement | undefined;
        // Expand the report copy only; preserve the user's open rows and selected tab.
        report?.querySelectorAll('[data-party-detail]').forEach(row => row.removeAttribute('hidden'));
        report?.querySelectorAll('[data-export-omit]').forEach(control => control.remove());
        const tableHtml = report?.innerHTML;
        if (!tableHtml) {
          throw new Error('Could not capture table HTML for PDF export.');
        }
        // Wrap the table HTML with basic html/body tags for Puppeteer
        // Add a specific class for the server to potentially target
        const htmlContent = `<html><head><title>Entity Export</title></head><body><div class="pdf-export-content">${tableHtml}</div></body></html>`;
        const documentName = documentMetadata?.fileName;
        result = await EntityExportService.exportToPdf(htmlContent, documentName);
        exportDescription = `Content exported to ${result.filename}`;
      }

      if (result) {
        EntityExportService.downloadBase64File(
          result.data,
          result.contentType,
          result.filename
        );
        toast({
          title: "Export successful",
          description: exportDescription,
        });
      }
    } catch (error) {
      console.error("Export Error:", error); // Log detailed error
      toast({
        title: "Export failed",
        description: error instanceof Error ? error.message : "Failed to export entities",
        variant: "destructive"
      });
    } finally {
      setIsExporting(null);
    }
  };

  const exportMenu = (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={
                  isExporting !== null
                  || (
                    entities.length === 0
                    && unresolvedVisualIdentities.length === 0 && humanReviews.length === 0
                  )
                }
                className="h-7 px-2 text-xs"
              >
                {isExporting !== null ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="h-3 w-3 mr-1" />
                    Export As
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={() => handleExport('json')} 
                disabled={isExporting !== null}
                className="text-xs"
              >
                <FileText className="mr-2 h-3 w-3" />
                JSON
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExport('md')} 
                disabled={isExporting !== null}
                className="text-xs"
              >
                <FileText className="mr-2 h-3 w-3" />
                Markdown
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleExport('pdf')} 
                disabled={isExporting !== null}
                className="text-xs"
              >
                <FileText className="mr-2 h-3 w-3" />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
  );

  return (
    <Collapsible defaultOpen={defaultOpen} className="entities-container">
      <div className="flex items-center justify-between py-2 mb-1">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold">{currentSectionTitle}</h2>
          <Badge variant="outline" className="ml-2 bg-background/40">
            {currentDisplayEntities.length} {currentDisplayEntities.length === 1 ? 'party' : 'parties'}
          </Badge>
        </div>

        <div className="flex items-center gap-1">
          {exportTarget ? createPortal(exportMenu, exportTarget) : exportMenu}
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ChevronDown className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-180" />
              <span className="sr-only">Toggle</span>
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>

      <CollapsibleContent ref={contentRef} forceMount className="data-[state=closed]:hidden">
        {/* Wrap the EntityTable in a div with the ref */}
        <div ref={tableContainerRef} className="pt-2 entity-table-export-container">
          {currentDisplayEntities.length > 0 && (
            <EntityTable entities={currentDisplayEntities} />
          )}
          {humanReviews.length > 0 && <section className="mt-3 rounded-md border p-3 ph-mask" data-sentry-mask aria-label="Human-reviewed names">
            <h3 className="font-medium">Confirmed names</h3>
            <p className="text-sm">Names a reviewer read from the document, with their screening result.</p>
            <ul className="space-y-3 mt-2">{humanReviews.map(review => <li key={review.id}>
              <p className="font-medium">{review.confirmedName}</p>
              <p className="text-sm">{screeningLabel(review)}</p>
              <p className="text-sm">{review.fileName} · page {review.reviewedPage}</p>
              <p className="text-xs">Confirmed by {review.reviewedBy} at {new Date(review.reviewedAt).toLocaleString()}</p>
              {review.screening.matches.map((match, index) => <p className="text-sm" key={index}>{match.source}: {match.details}</p>)}
              {review.retainedScreening && <div className="mt-1 rounded border border-red-300 p-2 text-sm" role="alert">
                <p>Previous screening still requires review ({new Date(review.retainedScreening.checkedAt).toLocaleString()}). The latest screening did not complete.</p>
                {review.retainedScreening.matches.map((match, index) => <p key={index}>{match.source}: {match.details}</p>)}
              </div>}
              {review.evidenceStatus !== 'available' && <p className="text-xs">Independent reading evidence was unavailable. This is a human transcription of the original source.</p>}
              <Button variant="outline" size="sm" className="mt-1" onClick={() => setSelectedSource({ identity: review.sourceIdentity, documentId: review.documentId, storageDocumentId: review.storageDocumentId, fileName: review.fileName, humanReview: review, orderId: orderId || '' })}>Review or correct</Button>
              {review.revision > 1 && <details className="mt-1 text-sm"><summary>Earlier decisions</summary>{humanHistory?.filter(item => item.documentId === review.documentId && item.sourceIdentity.occurrenceId === review.sourceIdentity.occurrenceId && item.revision < review.revision).map(item => <p key={item.id}>{item.confirmedName} · Revision {item.revision} · {screeningLabel(item)} · {item.reviewedBy} · {new Date(item.reviewedAt).toLocaleString()}</p>)}</details>}
            </li>)}</ul>
          </section>}
          {unresolvedSources.length > 0 && (
            <details className="mt-3 text-sm text-muted-foreground" aria-label="Names not read automatically">
              <summary className="cursor-pointer select-none">
                {unresolvedSources.length === 1 ? '1 name could not be read automatically' : `${unresolvedSources.length} names could not be read automatically`} and {unresolvedSources.length === 1 ? 'was' : 'were'} not screened
              </summary>
              <ul className="mt-2 divide-y rounded-md border text-foreground">
                {unresolvedSources.map(source => (
                  <li key={`${source.documentId}:${source.identity.occurrenceId}`} className="flex flex-wrap items-center justify-between gap-2 p-3">
                    <div className="min-w-0">
                      <p className="font-medium">{unresolvedVisualIdentityLabel(source.identity)} · {source.fileName || 'Source document'}, page {source.identity.pageNumber}</p>
                      <p className="text-muted-foreground">{sourceReviewReason[source.identity.resolutionCode]}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setSelectedSource({ ...source, orderId: orderId || '' })}>Check name</Button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </CollapsibleContent>
      {selectedSource && selectedSource.orderId === (orderId || '') && (
        <React.Suspense fallback={<p role="status">Loading source viewer…</p>}>
          <SourceEvidenceDialog key={`${selectedSource.orderId}:${selectedSource.documentId}:${selectedSource.identity.occurrenceId}`} selection={selectedSource} orderId={selectedSource.orderId} onClose={() => setSelectedSource(null)} />
        </React.Suspense>
      )}
    </Collapsible>
  );
}
